import { AxiosError } from 'axios'
import {
  EnumerationsAnalysisCategory,
  EnumerationsProportionalAnalyses,
  type TransferObjectsDataVizAnalysisFilterDataDTO,
} from 'client/sratool'
import type { ProjectGroup } from '~/constants/types/sra'
import type { ProjectAnalysisOption } from '~/constants/types/projectDataVisualizations'
import { ROUTE_NAME, STORE } from '~/constants/strings'
import { HNRV2 } from '~/helpers'
import { api } from '~/services'
import { notifyFailure } from '~/utils/notifications'
import { getStoreKey } from '~/helpers/store'
import { projectDataVizDateFormat } from '~/helpers/date'
import {
  applicantFlowTypes,
  isNonCompetitiveMovementsAnalysisType,
  isSelectionRateAnalysisType,
  laborMarketTypes,
  nonCompetitiveMovementsTypes,
} from '~/helpers/wfa'
import { useProjectDataVisualizationSelectionStore } from './projectDataVisualizations/projectDataVisualizationsSelection'

export const useProjectStore = defineStore(
  STORE.PROJECTS,
  () => {
    const ProjectDataVisualizationSelectionStore =
      useProjectDataVisualizationSelectionStore()

    const hydrated = ref(false)
    const loading = ref({
      projects: false,
    })

    const route = useRoute()

    const selectedProject = computed<
      ProjectGroup['projects'][number] | undefined
    >(() => {
      return projectOptions.value.find(
        (proj) =>
          proj.projectID === ProjectDataVisualizationSelectionStore.projectID
      )
    })
    const projectOptions = ref<ProjectGroup['projects']>([])

    const formattedSelectedProjectDates = computed(() => {
      return {
        snapshotDate: selectedProject.value?.snapshotDate
          ? projectDataVizDateFormat(selectedProject.value.snapshotDate)
          : '',
        transactionStartDate: selectedProject.value?.transactionStartDate
          ? projectDataVizDateFormat(selectedProject.value.transactionStartDate)
          : '',
        transactionEndDate: selectedProject.value?.transactionEndDate
          ? projectDataVizDateFormat(selectedProject.value.transactionEndDate)
          : '',
      }
    })

    const projectGroups = computed<ProjectGroup[]>(() => {
      const selectedProjectIndex = projectOptions.value.findIndex(
        (project) => project.projectID === selectedProject.value?.projectID
      )

      const otherProjects = [...projectOptions.value]
      if (selectedProjectIndex !== -1) {
        otherProjects.splice(selectedProjectIndex, 1)
      }

      return [
        {
          title: 'Selected Project',
          onListItemClick: ({ project, toggle }) => {
            ProjectDataVisualizationSelectionStore.updateSelection({
              project: project.projectID,
              analysis: null,
              proportionalAnalysisType: null,
              strata: null,
              strataVal: null,
              groupField: null,
            })
            toggle()
          },
          projects:
            selectedProjectIndex !== -1
              ? [projectOptions.value[selectedProjectIndex]]
              : [],
          noDataText: 'No Projects Selected',
        },
        {
          title: 'Available Projects',
          onListItemClick: ({ project, toggle }) => {
            ProjectDataVisualizationSelectionStore.updateSelection({
              project: project.projectID,
              analysis: null,
              proportionalAnalysisType: null,
              strata: null,
              strataVal: null,
              groupField: null,
            })
            toggle()
          },
          projects: otherProjects,
          noDataText: selectedProjectIndex
            ? 'No Other Projects found'
            : 'No Projects Found',
        },
      ]
    })

    const selectedAnalysis = computed<
      (typeof pageSpecificAnalysisOptions.value)[number] | undefined
    >(() => {
      return pageSpecificAnalysisOptions.value.find(
        (analysis) =>
          analysis.analysisID ===
            ProjectDataVisualizationSelectionStore.analysisID &&
          analysis.proportionalAnalysisID ===
            ProjectDataVisualizationSelectionStore.proportionalAnalysisTypeID
      )
    })

    const analysesForSelectedProject = ref<
      TransferObjectsDataVizAnalysisFilterDataDTO[]
    >([])

    const isProportionalAnalysis = computed(() => {
      return (
        selectedAnalysis.value?.analysisCategoryID ===
        EnumerationsAnalysisCategory.ProportionalAnalysis
      )
    })

    const proportionalAnalysisCategory = computed<
      'termination' | 'promotion' | undefined
    >(() => {
      if (!isProportionalAnalysis.value || !selectedAnalysis.value) {
        return
      }

      const terminationAnalysis: EnumerationsProportionalAnalyses[] = [
        EnumerationsProportionalAnalyses.AllTerminationsAnalysis,
        EnumerationsProportionalAnalyses.VoluntaryTerminationsAnalysis,
        EnumerationsProportionalAnalyses.InvoluntaryTerminationsAnalysis,
      ]
      const promotionAnalysis: EnumerationsProportionalAnalyses[] = [
        EnumerationsProportionalAnalyses.NonCompetitivePromotionsAnalysis,
      ]

      if (
        terminationAnalysis.includes(
          selectedAnalysis.value.proportionalAnalysisID
        )
      ) {
        return 'termination'
      }

      if (
        promotionAnalysis.includes(
          selectedAnalysis.value.proportionalAnalysisID
        )
      ) {
        return 'promotion'
      }
    })

    const getProjectSelection = async (): Promise<void> => {
      loading.value.projects = true
      const response = await HNRV2({
        fn: () => api.workforceAnalyticsProjects.apiSraProjectSelectorGet(),
      })

      loading.value.projects = false

      if (response instanceof AxiosError) {
        ProjectDataVisualizationSelectionStore.updateSelection({
          project: null,
          analysis: null,
          proportionalAnalysisType: null,
          strata: null,
          strataVal: null,
          groupField: null,
        })

        projectOptions.value = []
        analysesForSelectedProject.value = []
        notifyFailure('Problem getting your project selection')
        return
      }

      const noSelectedProject = !response.selectedProject
      const noProjectOptions = response.projects.length === 0

      if (noSelectedProject || noProjectOptions) {
        ProjectDataVisualizationSelectionStore.updateSelection({
          project: null,
          analysis: null,
          proportionalAnalysisType: null,
          strata: null,
          strataVal: null,
          groupField: null,
        })

        projectOptions.value = []
        analysesForSelectedProject.value = []
        return
      }

      const projectDetailsHaveChanged = Object.keys(
        response.selectedProject
      ).some(
        (key) =>
          response.selectedProject[
            key as keyof typeof response.selectedProject
          ] !==
          selectedProject.value?.[key as keyof typeof response.selectedProject]
      )

      const selectedProjectDifferent =
        (response.selectedProject &&
          response.selectedProject.projectID !==
            selectedProject.value?.projectID) ||
        projectDetailsHaveChanged

      if (selectedProjectDifferent) {
        ProjectDataVisualizationSelectionStore.updateSelection({
          project: response.selectedProject.projectID,
          analysis: null,
          proportionalAnalysisType: null,
          strata: null,
          strataVal: null,
          groupField: null,
        })
      }

      projectOptions.value = response.projects
      analysesForSelectedProject.value = response.analysesForSelectedProject
    }

    const setProjectSelection = async ({
      projectID,
    }: {
      projectID: string
    }): Promise<boolean> => {
      loading.value.projects = true
      const response = await HNRV2({
        fn: () =>
          api.workforceAnalyticsProjects.apiSraProjectSelectorProjectIDPost(
            projectID
          ),
      })
      loading.value.projects = false
      if (response instanceof AxiosError) {
        notifyFailure('Problem saving your project selection')
        return false
      }

      return true
    }

    const formattedAnalysisOptions = computed(() => {
      const distinctAnalyses = [
        ...new Set(
          analysesForSelectedProject.value.map(
            (analysis) => analysis.analysisID
          )
        ),
      ]
      const transformed: ProjectAnalysisOption[] =
        analysesForSelectedProject.value
          .map((analysis) => {
            return {
              ...analysis,
              uniqueID:
                analysis.analysisCategoryID ===
                  EnumerationsAnalysisCategory.ProportionalAnalysis &&
                analysis.proportionalAnalysisID !== null
                  ? `${analysis.analysisID}-${analysis.proportionalAnalysisID}`
                  : analysis.analysisID,
              label:
                analysis.analysisCategoryID ===
                  EnumerationsAnalysisCategory.ProportionalAnalysis &&
                analysis.proportionalAnalysisID !== null
                  ? `${
                      distinctAnalyses.length > 0
                        ? `${analysis.analysisName} - `
                        : ''
                    }${analysis.proportionalAnalysisName}`
                  : analysis.analysisName,
            }
          })
          .sort((a, b) => a.label.localeCompare(b.label))

      return transformed
    })

    const pageSpecificAnalysisOptions = computed<ProjectAnalysisOption[]>(
      () => {
        const routeName = route && route.name ? route.name : undefined

        switch (routeName) {
          case ROUTE_NAME.PROJECT_OVERVIEW_DATA_VISUALIZATIONS:
            return []
          case ROUTE_NAME.COMPETITIVE_MOVEMENTS_DATA_VISUALIZATIONS:
            return formattedAnalysisOptions.value.filter(
              isSelectionRateAnalysisType
            )
          case ROUTE_NAME.NON_COMPETITIVE_MOVEMENTS_DATA_VISUALIZATIONS:
            return formattedAnalysisOptions.value.filter(
              isNonCompetitiveMovementsAnalysisType
            )
          case ROUTE_NAME.WORKFORCE_INSIGHTS_DATA_VISUALIZATIONS:
            return getProportionalAnalysesWithoutProportionalAnalysisType(
              nonCompetitiveMovementsTypes
            )
          case ROUTE_NAME.RECRUITMENT_PROCESS_INSIGHTS_DATA_VISUALIZATIONS:
            return getProportionalAnalysesWithoutProportionalAnalysisType(
              applicantFlowTypes
            )
          case ROUTE_NAME.TALENT_AVAILABILITY_METRICS_DATA_VISUALIZATIONS:
            return getProportionalAnalysesWithoutProportionalAnalysisType(
              laborMarketTypes
            )
          default:
            return formattedAnalysisOptions.value
        }
      }
    )

    const getProportionalAnalysesWithoutProportionalAnalysisType = (
      allowedTypes: EnumerationsProportionalAnalyses[]
    ): ProjectAnalysisOption[] => {
      return [
        ...new Map(
          formattedAnalysisOptions.value
            .filter(
              (a) =>
                a.analysisCategoryID ===
                  EnumerationsAnalysisCategory.ProportionalAnalysis &&
                allowedTypes.includes(a.proportionalAnalysisID)
            )
            .map((analysis): [string, ProjectAnalysisOption] => [
              analysis.analysisID,
              {
                analysisID: analysis.analysisID,
                analysisTypeID: analysis.analysisTypeID,
                analysisName: analysis.analysisName,
                analysisCategoryID: analysis.analysisCategoryID,
                proportionalAnalysisID: analysis.proportionalAnalysisID,
                proportionalAnalysisName: analysis.proportionalAnalysisName,
                label: analysis.analysisName,
                uniqueID: analysis.uniqueID,
              },
            ])
        ).values(),
      ]
    }

    const analysisSelectionRequired = computed(() => {
      return route.name !== ROUTE_NAME.PROJECT_OVERVIEW_DATA_VISUALIZATIONS
    })

    const checkAnalysisValidity = (): boolean => {
      const existingSelectedAnalysisUniqueID = selectedAnalysis.value?.uniqueID
      const noAnalysisSelector = !analysisSelectionRequired.value
      const validAnalysisForNoneRequired =
        noAnalysisSelector && !existingSelectedAnalysisUniqueID

      const validSelectedAnalysis: boolean =
        !!pageSpecificAnalysisOptions.value.length &&
        !!pageSpecificAnalysisOptions.value.find(
          (analysis) => analysis.uniqueID === existingSelectedAnalysisUniqueID
        )

      return validAnalysisForNoneRequired || validSelectedAnalysis
    }

    const setDefaultAnalysis = (
      options: typeof pageSpecificAnalysisOptions.value
    ) => {
      if (!route.name || !hydrated.value) {
        return
      }

      const selectedAnalysisValid = checkAnalysisValidity()

      if (!selectedAnalysisValid) {
        ProjectDataVisualizationSelectionStore.updateSelection({
          project: ProjectDataVisualizationSelectionStore.projectID,
          analysis: options[0] ? options[0].analysisID : null,
          proportionalAnalysisType: options[0]
            ? options[0].proportionalAnalysisID
            : null,
          strata: null,
          strataVal: null,
          groupField: null,
        })
      }
    }

    const hasProjects = computed(() => {
      return projectOptions.value.length > 0
    })

    const hasAnalyses = computed(() => {
      return analysesForSelectedProject.value.length > 0
    })

    const hasValidProject = computed(() => {
      return (
        selectedProject.value !== null &&
        projectOptions.value.some(
          (p) => p.projectID === selectedProject.value?.projectID
        )
      )
    })

    const hasValidAnalysis = computed(() => {
      return checkAnalysisValidity()
    })

    watch(
      () => selectedProject.value,
      async (newSelectedProject, oldSelectedProject) => {
        if (!newSelectedProject?.projectID) {
          ProjectDataVisualizationSelectionStore.updateSelection({
            project: null,
            analysis: null,
            proportionalAnalysisType: null,
            strata: null,
            strataVal: null,
            groupField: null,
          })
          analysesForSelectedProject.value = []
          return
        }

        const projectChanged: boolean =
          !!newSelectedProject?.projectID &&
          newSelectedProject?.projectID !== oldSelectedProject?.projectID

        const selectionChangedDuringUserSession =
          projectChanged && hydrated.value

        if (selectionChangedDuringUserSession) {
          await setProjectSelection({ projectID: newSelectedProject.projectID })
          await getProjectSelection()
        }

        if (projectChanged) {
          if (pageSpecificAnalysisOptions.value.length === 0) {
            ProjectDataVisualizationSelectionStore.updateSelection({
              project: ProjectDataVisualizationSelectionStore.projectID,
              analysis: null,
              proportionalAnalysisType: null,
              strata: null,
              strataVal: null,
              groupField: null,
            })

            return
          }
          setDefaultAnalysis(pageSpecificAnalysisOptions.value)
        }
      }
    )

    watch(
      () => pageSpecificAnalysisOptions.value,
      (newAnalysisOptions) => {
        setDefaultAnalysis(newAnalysisOptions)
      }
    )

    const displayACPCustomFooter = computed<boolean>(() => {
      return (
        !!selectedProject.value?.attorneyClientPrivilege &&
        !!selectedProject.value.acpNotice
      )
    })

    return {
      hydrated,
      loading,

      selectedProject,
      projectOptions,
      formattedProjectDates: formattedSelectedProjectDates,
      projectGroups,
      displayACPCustomFooter,

      selectedAnalysis,
      isProportionalAnalysis,
      proportionalAnalysisCategory,
      analysesForSelectedProject,
      formattedAnalysisOptions,
      pageSpecificAnalysisOptions,
      analysisSelectionRequired,

      hasProjects,
      hasAnalyses,
      hasValidProject,
      hasValidAnalysis,

      getProjectSelection,
      setProjectSelection,
    }
  },
  {
    persist: {
      key: getStoreKey(STORE.PROJECTS),
      pick: [
        'selectedProject',
        'projectOptions',
        'projectGroups',
        'selectedAnalysis',
        'analysesForSelectedProject',
        'analysisOptions',
      ],
    },
  }
)
